import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactGa from "react-ga4";
import { CircularProgress as JoyCircularProgress } from "@mui/joy";
import styles from "../../styles/winterpay.module.css";
import { ReactComponent as Logo } from "../../svg/newlogo.svg";
import { track, getDistinctId, getAttributionProps } from "../../lib/mixpanel";
import { WinterConfig, WinterStudentType, benefitMet, calcCustomerPrice, dayNum, daysInMonth, fromDayNum, toDate, ymd, won, pad2, programCell, startRangeOf } from "./price";
import {
    cx, Benefit, BenefitCard, CalCell, Calendar, ContactSheet, DiscountRows, DoneIcon, Dropdown, InfoRows, InviteCard, ProofZoom, SegGroup, TimerBar, WaitPop,
    IconBack, IconCalendar, IconCheck, IconChevronDown, IconChevronRight, IconClock, IconInfo, IconLock, IconPerson, IconRefresh, IconSeat, IconUpload, IconZoom,
} from "./sections";
import { revealEl, useIntroOnce, useKeyboardFit, useNavGuards } from "./hooks";
import { isPreviewMode, previewConfig, previewLink } from "./previewMock";

/**
 * 윈터스쿨 셀프 결제 (/winterpay?id=&code=) — 디자인 "결제링크 사전예약" 고객 화면
 *  링크(winterPayLink)가 있을 때만 열린다. 고객이 지점·학생구분·프로그램·시작/종료일·학생정보·결제수단을 고르면
 *  서버(peetsunbae.com/dashboard/payments/winterpay/submit)가 금액을 계산해 newPayLink 를 만들고
 *  카드는 결제선생 링크로 보내고, 계좌이체는 입금 대기(계좌 안내 + 이체 내역 업로드 → 제출) → 확인 대기로 이어진다.
 *  금액 계산은 ./price.ts (서버와 같은 코드). 인증번호는 registerlink 와 같은 suneungsunbae.com/api/booking/cert.
 *  만료: 서버가 /data 를 expired 로 거절하면 차단 화면, 보는 중에 0이 되면 결제·업로드·제출 비활성(서버도 isLate/expired 로 거절).
 */

const API = "https://peetsunbae.com/dashboard/payments";
const CERT_API = "https://suneungsunbae.com/api/booking/cert";
const W = ["일", "월", "화", "수", "목", "금", "토"];
const digits = (v: any) => String(v == null ? "" : v).replace(/\D/g, "");
const phoneOk = (v: string) => /^010\d{8}$/.test(digits(v));
const fmtPhone = (v: string) => { const d = digits(v).slice(0, 11); return d.length > 7 ? d.slice(0, 3) + "-" + d.slice(3, 7) + "-" + d.slice(7) : d.length > 3 ? d.slice(0, 3) + "-" + d.slice(3) : d; };
const dshort = (d: Date) => d.getFullYear() + "." + pad2(d.getMonth() + 1) + "." + pad2(d.getDate()) + "(" + W[d.getDay()] + ")";
const md2 = (d: Date) => (d.getMonth() + 1) + "월 " + d.getDate() + "일(" + W[d.getDay()] + ")";
const dtLong = (ms: number) => { const d = new Date(ms); return d.getFullYear() + "." + pad2(d.getMonth() + 1) + "." + pad2(d.getDate()) + " " + pad2(d.getHours()) + ":" + pad2(d.getMinutes()); };
//마감 문구 — 디자인은 "10/6(화) 오후 8시"를 고정값으로 썼고, 여기선 링크 만료시각(서버)으로 같은 형식을 만든다
const ampm = (d: Date) => { const h = d.getHours(), ap = h < 12 ? "오전" : "오후", h12 = h % 12 || 12; return { ap, h12 }; };
const dueMarkOf = (d: Date) => { const { ap, h12 } = ampm(d); return (d.getMonth() + 1) + "/" + d.getDate() + "(" + W[d.getDay()] + ") " + ap + " " + (d.getMinutes() ? h12 + ":" + pad2(d.getMinutes()) : h12 + "시"); };
const dueAbsOf = (d: Date) => { const { ap, h12 } = ampm(d); return (d.getMonth() + 1) + "월 " + d.getDate() + "일 " + ap + " " + h12 + ":" + pad2(d.getMinutes()); };

export interface LinkInfo {
    id: number; location: string | null; studentType: string | null; dueDate: number; status: string; paidAt: number | null;
    program: string | null; startDate: string | null; endDate: string | null; price: number | null; paymentMethod: string | null;
    whichCertify: string | null; studentName: string | null; gender: string | null; studentPhoneNumber: string | null; parentPhoneNumber: string | null;
    newPayLinkId: number | null; newPayLinkCode: number | null; imageSrc: string | null; shortUrl: string | null;
    /** 윈터 랜딩 '예약 링크만 먼저 받기'(POST /winterpay/book) 링크만 — 예약 순번·받는 사람 이름 */
    reservationNo?: number | null; name?: string | null;
    priceDetail?: { price: number | null; original: number | null; usedBenefits: { id: string; name: string; pct: number }[] } | null;
    sample?: boolean;
}

type Stage = "pay" | "transfer" | "waiting" | "done";

const WinterPay: React.FC = () => {

    useLayoutEffect(() => { window.scrollTo(0, 0); }, []);

    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const id = Number(params.get("id"));
    const code = Number(params.get("code"));
    //예시 페이지(?sample=1): 서버 행 없이 설정만 받아 각자 브라우저에서 입력만 해 봄. 결제·제출 없음, 마감은 서버 고정값
    const sample = params.get("sample") === "1";
    const rootRef = useRef<HTMLDivElement | null>(null);

    //로딩·링크
    const [loadState, setLoadState] = useState<"loading" | "error" | "ready">("loading");
    const [errorKind, setErrorKind] = useState<string>("");
    const [expiredReserved, setExpiredReserved] = useState(false);
    const [config, setConfig] = useState<WinterConfig | null>(null);
    const [link, setLink] = useState<LinkInfo | null>(null);
    const [clockOffset, setClockOffset] = useState(0); // 서버시각 - 클라이언트시각
    const [stage, setStage] = useState<Stage>("pay");

    //입력
    const [branch, setBranch] = useState("");
    const [type, setType] = useState<WinterStudentType>("general");
    const [program, setProgram] = useState<string | null>(null);
    //고객이 원래 고른 프로그램(링크 또는 라디오). 마감 팝업이 프리로 돌려놔도 지점을 바꾸면 이 값으로 복귀 → 다른 마감 지점에서도 팝업이 다시 뜬다
    const [wantProgram, setWantProgram] = useState<string | null>(null);
    const [start, setStart] = useState<Date | null>(null);
    const [end, setEnd] = useState<Date | null>(null);
    const [name, setName] = useState("");
    const [gender, setGender] = useState<"male" | "female">("male");
    const [otpTarget, setOtpTarget] = useState<"phone" | "parent">("parent"); // 인증받을 번호: 학부모(parent, 기본) / 학생(phone)
    const [studentPhone, setStudentPhone] = useState("");
    const [parentPhone, setParentPhone] = useState("");
    const [verified, setVerified] = useState<string[]>([]);
    const [otpKey, setOtpKey] = useState<string | null>(null);
    const [otpCode, setOtpCode] = useState("");
    const [otpErr, setOtpErr] = useState("");
    const [payMethod, setPayMethod] = useState<"card" | "transfer">("card");

    //UI
    const [openDd, setOpenDd] = useState<string | null>(null);
    const [openRow, setOpenRow] = useState<"start" | "end" | null>(null);
    const [view, setView] = useState<{ y: number; m: number } | null>(null);
    const [showRows, setShowRows] = useState(false);
    const [payTried, setPayTried] = useState(false);
    const [contactOn, setContactOn] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitHint, setSubmitHint] = useState("");

    //계좌이체
    const [transfer, setTransfer] = useState<{ newPayLinkId: number; newPayLinkCode: number; bankinfo: any; price: number } | null>(null);
    const [proofUrl, setProofUrl] = useState<string | null>(null);
    const [proofUploading, setProofUploading] = useState(false);
    const [proofUploaded, setProofUploaded] = useState(false);
    const [proofError, setProofError] = useState("");
    const [proofTried, setProofTried] = useState(false);
    const [proofZoom, setProofZoom] = useState(false);
    const [finishLoading, setFinishLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const codeInputRef = useRef<HTMLInputElement | null>(null);

    const intro = useIntroOnce();
    useKeyboardFit(rootRef);

    //----- 데이터 로드 -----
    const applyLink = (cfg: WinterConfig, lk: LinkInfo) => {
        setConfig(cfg);
        setLink(lk);
        setClockOffset(cfg.now - Date.now());
        if (lk.location) setBranch(lk.location);
        if (lk.studentType === "student" || lk.studentType === "general") setType(lk.studentType);
        //이미 제출한 내용이 있으면 본인이 입력한 값 복원 (링크 자체의 이름·전화는 넣지 않음)
        if (lk.program) { setProgram(lk.program); setWantProgram(lk.program); }
        if (lk.startDate) setStart(toDate(lk.startDate));
        if (lk.endDate) setEnd(toDate(lk.endDate));
        if (lk.studentName) setName(lk.studentName);
        if (lk.gender === "male" || lk.gender === "female") setGender(lk.gender);
        if (lk.studentPhoneNumber) setStudentPhone(digits(lk.studentPhoneNumber));
        if (lk.parentPhoneNumber) setParentPhone(digits(lk.parentPhoneNumber));
        if (lk.whichCertify === "student" || lk.whichCertify === "parent") {
            setOtpTarget(lk.whichCertify === "student" ? "phone" : "parent");
            const num = lk.whichCertify === "student" ? lk.studentPhoneNumber : lk.parentPhoneNumber;
            if (num) setVerified([digits(num)]);
        }
        if (lk.paymentMethod === "withdrawal") setPayMethod("transfer");
        if (lk.paymentMethod === "payteacher") setPayMethod("card");
        if (lk.newPayLinkId && lk.newPayLinkCode && lk.price) {
            const b = cfg.branches.find(x => x.english === lk.location);
            setTransfer({ newPayLinkId: lk.newPayLinkId, newPayLinkCode: lk.newPayLinkCode, bankinfo: b ? b.bankinfo : null, price: lk.price });
        }
        if (lk.imageSrc) { setProofUrl(lk.imageSrc.startsWith("http") || lk.imageSrc.startsWith("blob:") || lk.imageSrc.startsWith("data:") ? lk.imageSrc : "https://peetsunbae.com" + lk.imageSrc); setProofUploaded(true); }
        if (lk.status === "paid") setStage("done");
        else if (lk.status === "transferFinished") setStage("waiting");
        else if (lk.status === "transferUploaded") setStage("transfer");
        else setStage("pay");
    };

    const loadData = async () => {
        //로컬 미리보기(localhost + ?preview=1): 서버 없이 목 설정으로 화면만 확인. 제출·인증은 실제 서버로 가므로 동작하지 않음
        if (isPreviewMode()) {
            const cfg = previewConfig();
            const lk = previewLink(cfg);
            if (!lk) { setErrorKind(params.get("stage") || "expired"); setLoadState("error"); return; }
            applyLink(cfg, lk);
            setLoadState("ready");
            return;
        }
        if (!sample && (!id || !code)) { setErrorKind("noValidCode"); setLoadState("error"); return; }
        try {
            const resp = await fetch(sample ? `${API}/winterpay/sample` : `${API}/winterpay/data?id=${id}&code=${code}`);
            const result = await resp.json();
            if (result.message !== "success") {
                setErrorKind(result.message || "error");
                setExpiredReserved(!!result.reserved);
                setLoadState("error");
                track("Winter Pay Link Blocked", { form_type: "winterpay", payment_id: id, sample, reason: result.message });
                return;
            }
            applyLink(result.config, result.link);
            setLoadState("ready");
            track("Winter Pay Page Viewed", { form_type: "winterpay", payment_id: id, sample, status: result.link.status });
        } catch (e) {
            console.log(e);
            setErrorKind("error");
            setLoadState("error");
        }
    };

    useEffect(() => {
        ReactGa.send({ hitType: "pageview", page_title: "winterpay" });
        loadData();
        const tick = setInterval(() => setNow(Date.now()), 1000);
        const onRz = () => setVw(window.innerWidth);
        window.addEventListener("resize", onRz);
        const onDown = (e: MouseEvent) => { const t = e.target as HTMLElement; if (!(t.closest && t.closest("[data-dd]"))) setOpenDd(null); };
        document.addEventListener("mousedown", onDown);
        return () => { clearInterval(tick); window.removeEventListener("resize", onRz); document.removeEventListener("mousedown", onDown); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //----- 파생값 -----
    const serverNow = now + clockOffset;
    const today = config ? config.today : ymd(new Date(serverNow));
    const todayN = dayNum(toDate(today));
    const linkSec = link ? Math.max(0, Math.floor((link.dueDate - serverNow) / 1000)) : 0;
    const expired = !!link && linkSec <= 0;

    //입금 대기 뒤로가기 복귀 — 훅이라 분기 많은 계산보다 앞에 둔다 (이탈 방지 시트는 제거함)
    useNavGuards({ stage, onBackToPay: () => setStage("pay") });

    const linkTo = link ? link.dueDate - clockOffset : 0; // 로컬 시계 기준 만료 시각 (Countdown 용)
    const dueDateObj = link ? new Date(link.dueDate) : new Date();
    const dueMark = dueMarkOf(dueDateObj);
    const under10 = linkSec > 0 && linkSec < 600;

    const programs = useMemo(() => (config ? config.programs.filter(p => dayNum(toDate(p.startTo)) >= todayN) : []), [config, todayN]);
    //지점×프로그램 모집 상태(대시보드 '지점별 마감관리') — 마감(closed)이면 결제 불가 → 마감 팝업(WaitPop). 기본 선택은 마감 아닌 프로그램부터
    const isClosedProg = (p: { pid: string } | null) => { const c = p ? programCell(config, branch, p.pid) : null; return !!c && c.status === "closed"; };
    const prog = programs.find(p => p.pid === program) || programs.find(p => !isClosedProg(p)) || programs[0] || null;
    const pid = prog ? prog.pid : null;
    const progClosed = !!branch && isClosedProg(prog);
    //시작 범위 — 지점별 설정(startRangeOf)이 공통값보다 우선. fixed 면 고정일 하나
    const range = startRangeOf(config, prog, branch);
    const fixed = range && range.fixed ? range.from : null;
    const minN = range ? Math.max(todayN, dayNum(toDate(range.from))) : todayN;
    const maxN = range ? dayNum(toDate(range.to)) : todayN;
    const st0 = fixed ? toDate(fixed) : (start && dayNum(start) >= minN && dayNum(start) <= maxN ? start : null);
    const endOpts = prog ? prog.ends.map(toDate) : [];
    const en0 = st0 && end && endOpts.some(e => dayNum(e) === dayNum(end)) && dayNum(end) >= dayNum(st0) ? end : null;
    const ready = !!(config && branch && st0 && en0);
    const calc = ready && config ? calcCustomerPrice(config.rates, type, branch, st0, en0, today) : null;
    const price = calc ? calc.price : null;
    const original = calc ? calc.original : null;
    const branchInfo = config ? config.branches.find(b => b.english === branch) : undefined;
    const branchKo = branchInfo ? branchInfo.korean : "";

    //프리윈터 좌석 선택권 (11/23~12/12 입실자)
    const seatLastN = config ? dayNum(toDate(config.seatPerk.lastStart)) : 0;
    const seatPerkOn = !!config && programs.some(p => p.pid === "p2") && todayN <= seatLastN;
    const seatOk = seatPerkOn && pid === "p2" && !!st0 && dayNum(st0) <= seatLastN;
    const seatLate = seatPerkOn && pid === "p2" && !!st0 && dayNum(st0) > seatLastN;

    //프로그램 구간(프리/정규) 경계 = 정규윈터 최소 시작일 전날
    const regP = config ? config.programs.find(p => p.pid === "p3") : undefined;
    const regStartN = regP ? dayNum(toDate(regP.startFrom)) : Infinity;
    const preDays = st0 && en0 ? Math.max(0, Math.min(dayNum(en0), regStartN - 1) - dayNum(st0) + 1) : 0;
    const regDays = st0 && en0 ? Math.max(0, dayNum(en0) - Math.max(dayNum(st0), regStartN) + 1) : 0;
    const spanName = preDays && regDays ? "프리윈터 + 정규윈터" : regDays ? "정규윈터" : preDays ? "프리윈터" : (prog ? prog.name : "");
    const splitTxt = st0 && en0 ? dshort(st0) + " ~ " + dshort(en0) + " · " + (preDays && regDays ? "프리 " + preDays + "일 + 정규 " + regDays + "일" : "총 " + (preDays + regDays) + "일") : "";
    const periodTxt = st0 && en0 ? dshort(st0) + " ~ " + dshort(en0) : "-";

    //연락처 인증 — 학생/학부모 중 아무 번호 하나만 인증하면 됨. 칸 순서 고정(학생 위·학부모 아래), 인증은 아래 '인증받을 번호' 블록에서
    const isVerified = (num: string) => phoneOk(num) && verified.includes(digits(num));
    const anyVerified = isVerified(digits(studentPhone)) || isVerified(digits(parentPhone));
    const otpNum = digits(otpTarget === "phone" ? studentPhone : parentPhone);
    const otpOk = isVerified(otpNum);
    const otpSent = !otpOk && otpKey === otpTarget + ":" + otpNum;

    const missing: [boolean, string, string, ("start" | "end")?][] = [
        [!prog, "선택할 수 있는 프로그램이 없어요", "branch"],
        [progClosed, "모집이 마감된 프로그램이에요", "branch"],
        [!branch, "지점을 선택해주세요", "branch"],
        [!st0, "등록 시작일을 선택해주세요", "period", "start"],
        [!!st0 && !en0, "등록 종료일을 선택해주세요", "period", "end"],
        [!name.trim(), "학생 이름을 입력해주세요", "contact"],
        [!phoneOk(studentPhone), "학생 연락처를 입력해주세요", "contact"],
        [!phoneOk(parentPhone), "학부모 연락처를 입력해주세요", "contact"],
        [!anyVerified, "학생 또는 학부모 연락처 중 하나를 인증해주세요", "contact"],
    ];
    const firstMissing = missing.find(([bad]) => bad) || null;
    const canPay = !firstMissing && price != null && !expired;

    //시작일 달력·종료일 목록이 펼쳐질 때 화면 아래에 가려져 있으면 스크롤로 드러냄
    const openPicker = (row: "start" | "end") => {
        setOpenRow(row);
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const el = document.querySelector(row === "start" ? "[data-sec=period] [data-cal]" : "[data-sec=period] [data-endlist]") as HTMLElement | null;
            revealEl(el, false);
        }));
    };

    //(훅은 모두 위에서 호출됨) 로컬 미리보기에서만 ?frame=m|t|p 로 프레임 강제 (창을 600px 아래로 못 줄이는 환경용)
    const forcedFrame = isPreviewMode() ? params.get("frame") : null;
    const frame = forcedFrame === "m" || forcedFrame === "t" || forcedFrame === "p" ? forcedFrame : vw >= 1080 ? "p" : vw >= 600 ? "t" : "m";

    //----- 달력 -----
    const calView = (() => { const first0 = fromDayNum(minN); return view || { y: first0.getFullYear(), m: first0.getMonth() }; })();
    const seatCal = seatPerkOn && pid === "p2";
    const calCells: CalCell[] = (() => {
        const { y, m } = calView;
        const first = new Date(y, m, 1).getDay(), days = daysInMonth(y, m);
        const fixedN = fixed ? dayNum(toDate(fixed)) : null;
        const cells: CalCell[] = [];
        const empty: CalCell = { day: "", empty: true, off: true, outside: false, sel: false, today: false, seat: false, onClick: null };
        for (let i = 0; i < first; i++) cells.push(empty);
        for (let d = 1; d <= days; d++) {
            const dt = new Date(y, m, d), n = dayNum(dt), col = (first + d - 1) % 7;
            const outside = n < minN || n > maxN || (fixedN != null && n !== fixedN);
            const off = outside || col === 0;
            cells.push({
                day: d, empty: false, off, outside, sel: !!st0 && dayNum(st0) === n, today: n === todayN,
                seat: seatCal && !off && n <= seatLastN,
                onClick: off ? null : () => { setStart(dt); setEnd(prev => (prev && dayNum(prev) >= n ? prev : null)); openPicker("end"); },
            });
        }
        while (cells.length < 42) cells.push(empty);
        return cells;
    })();
    const minD = fromDayNum(minN), maxD = fromDayNum(maxN);
    const calIdx = calView.y * 12 + calView.m;
    const calGo = (k: number) => { const d = new Date(calView.y, calView.m + k, 1); setView({ y: d.getFullYear(), m: d.getMonth() }); };

    //----- 인증번호 -----
    const sendCert = async (num: string, key: string) => {
        if (isVerified(num)) return;
        if (!phoneOk(num)) { setOtpKey(null); setOtpErr(""); setPayTried(true); return; }
        setOtpKey(key + ":" + num); setOtpCode(""); setOtpErr("");
        try {
            const resp = await fetch(`${CERT_API}?telephoneNumber=${num}`, { method: "GET" });
            const result = await resp.json();
            console.log(result);
            track("Phone Verification Requested", { form_type: "winterpay", payment_id: id, sample });
        } catch (e) { console.log(e); }
        setTimeout(() => { if (codeInputRef.current) codeInputRef.current.focus(); }, 50);
    };
    //진행 단계 기록(대시보드 신규 결제링크 '진행 단계') — 실패해도 흐름에 영향 없음. 예시 페이지는 기록 안 함
    const trackStage = (event: "certified" | "payClick") => {
        if (sample || !id || !code) return;
        try {
            fetch(`${API}/winterpay/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id, code, event }) }).catch(() => {});
        } catch (e) { /* ignore */ }
    };

    const confirmCert = async (num: string, v: string) => {
        if (v.length !== 6) { setOtpErr("인증번호 6자리를 입력해주세요."); return; }
        try {
            const resp = await fetch(CERT_API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ telephoneNumber: num, certNumber: v }) });
            const result = await resp.json();
            if (result.message === "success") {
                setVerified(prev => [...prev, num]); setOtpKey(null); setOtpCode(""); setOtpErr("");
                track("Phone Verification Succeeded", { form_type: "winterpay", payment_id: id, sample });
                trackStage("certified");
                if (codeInputRef.current) codeInputRef.current.blur();
            } else {
                setOtpErr("인증번호가 맞지 않아요. 다시 확인해주세요.");
                track("Phone Verification Failed", { form_type: "winterpay", payment_id: id, sample });
            }
        } catch (e) { console.log(e); setOtpErr("인증 확인 중 오류가 났어요. 다시 시도해주세요."); }
    };

    //----- 제출 -----
    const scrollToSec = (sec: string) => {
        const el = document.querySelector(`[data-sec="${sec}"]`);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 60, behavior: "smooth" });
    };
    const onPay = async () => {
        setSubmitHint("");
        if (sample) return; // 예시 페이지: 결제 버튼 비활성, 제출 경로 없음
        trackStage("payClick");
        if (expired) { setSubmitHint("사전 예약 결제 기간이 끝났어요"); return; }
        if (!canPay || !config || !st0 || !en0 || !prog) {
            setPayTried(true);
            if (firstMissing) { if (firstMissing[3]) setOpenRow(firstMissing[3]); scrollToSec(firstMissing[2]); }
            track("Winter Pay Attempted", { form_type: "winterpay", payment_id: id, sample, blocked: firstMissing ? firstMissing[1] : "noPrice" });
            return;
        }
        if (submitLoading) return;
        setSubmitLoading(true);
        try {
            const body = {
                id, code, location: branch, studentType: type, program: prog.pid, startDate: ymd(st0), endDate: ymd(en0),
                name: name.trim(), gender, studentPhoneNumber: digits(studentPhone), parentPhoneNumber: digits(parentPhone), whichCertify: isVerified(digits(studentPhone)) ? "student" : isVerified(digits(parentPhone)) ? "parent" : (otpTarget === "phone" ? "student" : "parent"),
                paymentMethod: payMethod, clientPrice: price, mp_distinct_id: getDistinctId(), mp_attribution: getAttributionProps(),
            };
            const resp = await fetch(`${API}/winterpay/submit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
            const result = await resp.json();
            console.log(result);
            setSubmitLoading(false);
            if (result.message === "priceMismatch") {
                if (result.config) { setConfig(result.config); setClockOffset(result.config.now - Date.now()); }
                setSubmitHint("금액 기준이 바뀌어 다시 계산했어요. 금액을 확인하고 다시 눌러주세요.");
                setPayTried(true);
                track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: "priceMismatch" });
                return;
            }
            if (result.message === "isLate") { setSubmitHint("사전 예약 결제 기간이 끝났어요"); track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: "expired" }); return; }
            if (result.message === "closed") { if (result.config) setConfig(result.config); setSubmitHint("모집이 마감된 프로그램이에요"); track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: "closed" }); return; }
            if (result.message === "wrongDate") { setSubmitHint("선택한 시작일은 지금 등록할 수 없어요. 시작일을 다시 골라주세요."); setStart(null); setEnd(null); setPayTried(true); track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: "wrongDate" }); return; }
            if (result.message === "isPaid") { await loadData(); return; }
            if (result.message === "destroyed") { setErrorKind("destroyed"); setLoadState("error"); return; }
            if (result.message !== "success") {
                setSubmitHint("오류가 났어요. 잠시 후 다시 시도하거나 상담으로 문의해주세요.");
                track("Winter Pay Submit Failed", { form_type: "winterpay", payment_id: id, reason: result.message });
                return;
            }
            track("Winter Pay Submitted", { form_type: "winterpay", payment_id: id, payment_method: payMethod, price, branch, program: prog.pid });
            if (result.transfer) {
                setTransfer({ newPayLinkId: result.newPayLinkId, newPayLinkCode: result.newPayLinkCode, bankinfo: result.bankinfo, price: result.price });
                if (result.imageSrc) { setProofUrl("https://peetsunbae.com" + result.imageSrc); setProofUploaded(true); }
                setStage("transfer");
                window.scrollTo(0, 0);
                track("Payment Bank Info Shown", { form_type: "winterpay", payment_id: id });
                return;
            }
            if (result.shortURL) {
                track("Payment Card Redirect", { form_type: "winterpay", payment_id: id, payment_method: "card" });
                window.location.href = result.shortURL;
            }
        } catch (e) {
            console.log(e);
            setSubmitLoading(false);
            setSubmitHint("오류가 났어요. 잠시 후 다시 시도하거나 상담으로 문의해주세요.");
        }
    };

    //----- 계좌이체: 이체 내역 업로드 · 제출 -----
    const uploadProof = async (file: File) => {
        if (!transfer || expired) return;
        setProofError(""); setProofUploading(true); setProofUploaded(false);
        setProofUrl(URL.createObjectURL(file));
        track("Receipt Upload Started", { form_type: "winterpay", payment_id: id, file_size: file.size });
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("data", JSON.stringify({ id: transfer.newPayLinkId, paymentMethod: "withdrawal", code: transfer.newPayLinkCode }));
            const resp = await fetch(`${API}/uploadpaylinkimg`, { method: "POST", body: form });
            const result = await resp.json();
            if (result.message === "success") {
                setProofUploaded(true);
                track("Receipt Upload Succeeded", { form_type: "winterpay", payment_id: id });
            } else {
                setProofUrl(null);
                setProofError(result.message === "expired" ? "결제 링크가 닫혀 올릴 수 없어요." : result.message === "destroyed" ? "파기된 링크예요." : "업로드에 실패했어요. 다시 시도해주세요.");
                track("Receipt Upload Failed", { form_type: "winterpay", payment_id: id, reason: result.message });
            }
        } catch (e) {
            console.log(e);
            setProofUrl(null);
            setProofError("업로드에 실패했어요. 다시 시도해주세요.");
        }
        setProofUploading(false);
    };
    const submitProof = async () => {
        if (expired) return;
        if (!proofUploaded || !proofUrl) { setProofTried(true); setTimeout(() => setProofTried(false), 2400); return; }
        if (!transfer || finishLoading) return;
        setFinishLoading(true);
        try {
            const resp = await fetch(`${API}/finishlinkpayment`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: transfer.newPayLinkId, code: transfer.newPayLinkCode }) });
            const result = await resp.json();
            if (result.message === "success") {
                track("Payment Self Reported", { form_type: "winterpay", payment_id: id });
                setStage("waiting");
                window.scrollTo(0, 0);
            } else {
                setProofError("처리에 실패했어요. 다시 시도해주세요.");
            }
        } catch (e) { console.log(e); setProofError("처리에 실패했어요. 다시 시도해주세요."); }
        setFinishLoading(false);
    };
    const copyAccount = async () => {
        if (!transfer || !transfer.bankinfo) return;
        try { await navigator.clipboard.writeText(String(transfer.bankinfo.accountNumber).replace(/-/g, "")); } catch (e) { console.log(e); }
        setCopied(true); setTimeout(() => setCopied(false), 1600);
    };

    //----- 화면 -----
    const pageClass = cx(styles.page, frame === "t" && styles.frameT, frame === "p" && styles.frameP);
    const Header = (
        <header className={styles.hdr} data-pay-hdr="">
            {stage === "transfer"
                ? <button type="button" className={styles.hdrBack} aria-label="결제 화면으로 돌아가기" onClick={() => { setStage("pay"); window.scrollTo(0, 0); }}><IconBack /></button>
                : <span className={styles.logo} aria-label="수능선배"><Logo /></span>}
        </header>
    );

    if (loadState === "loading") {
        return <div className={pageClass} data-pay="" ref={rootRef}><div className={styles.inner}>{Header}<div className={styles.loading}><JoyCircularProgress size="lg" thickness={6} /></div></div></div>;
    }

    //차단 화면 — 만료·파기·잘못된 접근 (registerlink 와 같이 결제 화면을 그리지 않음)
    if (loadState === "error" || !config || !link) {
        const msg = errorKind === "expired" ? (expiredReserved ? "1시간이 지나 예약 순번이 사라졌어요. 윈터스쿨 페이지에서 다시 신청하면 새 순번을 받아요" : "사전 예약 결제 기간이 끝났어요. 조기 마감 시 정규 예약은 오픈되지 않아요") : errorKind === "destroyed" ? "파기된 링크예요" : errorKind === "noValidCode" || errorKind === "noData" ? "잘못된 접근이에요" : "링크를 불러오지 못했어요";
        const sub = errorKind === "expired" && expiredReserved ? "" : errorKind === "expired" || errorKind === "destroyed" ? "담당자에게 문의해 주세요." : "링크 주소를 다시 확인하거나 상담으로 문의해주세요.";
        return (
            <div className={pageClass} data-pay="" ref={rootRef}><div className={styles.inner}>
                {Header}
                {contactOn && <ContactSheet isPay={false} onClose={() => setContactOn(false)} />}
                <div className={styles.errorScreen}>
                    <div className={styles.errorBanner}><IconClock />{msg}</div>
                    {sub && <p className={styles.errorSub}>{sub}</p>}
                    {errorKind === "expired" && expiredReserved && <a href="/winter" className={styles.errorPrimary}>윈터스쿨 페이지에서 다시 신청하기</a>}
                    <button type="button" className={styles.secondaryBtn} onClick={() => setContactOn(true)}>상담 문의하기</button>
                </div>
            </div></div>
        );
    }

    //완료·입금 화면에 쓰는 금액 정보 — 재방문이면 서버 priceDetail(결제 당시 혜택), 아니면 방금 계산값
    const pd = link.priceDetail && link.price ? { price: link.price, original: link.priceDetail.original, usedBenefits: link.priceDetail.usedBenefits || [] } : calc ? { price: calc.price, original: calc.original, usedBenefits: calc.usedBenefits } : null;
    const finalPrice = transfer ? transfer.price : (pd && pd.price != null ? pd.price : price);
    const anc = !!pd && pd.price != null && pd.original != null && pd.original > pd.price;
    const dName = pd && pd.usedBenefits.length ? pd.usedBenefits.map(c => c.name).join(" · ") + " 할인" : "할인";
    const discPct = anc && pd ? Math.round((1 - (pd.price as number) / (pd.original as number)) * 100) : 0;
    const doneOrig = anc && pd ? won(pd.original as number) : "";
    const doneDisc = anc && pd ? "−" + won((pd.original as number) - (pd.price as number)) : "";
    const totalTxt = finalPrice == null ? "-" : won(finalPrice);
    const nm = name.trim();
    const infoRows: [string, string][] = ([["프로그램", "윈터스쿨 " + (spanName || "-")]] as [string, string][])
        .concat(seatOk ? [["혜택", "희망 좌석 선택권"]] : [])
        .concat([["지점", branchKo || "-"], ["등록 기간", periodTxt], ["학생 연락처", fmtPhone(studentPhone) || "-"], ["학부모 연락처", fmtPhone(parentPhone) || "-"]]);
    const AskRow = (
        <div className={styles.askRow}><span>등록 내용이 다르거나 궁금한 점이 있나요?</span><button type="button" className={styles.askBtn} onClick={() => setContactOn(true)}>문의하기</button></div>
    );

    //----- 결제 완료 (카드) -----
    if (stage === "done") {
        return (
            <div className={pageClass} data-pay="" ref={rootRef}><div className={cx(styles.inner, intro && styles.introIn)}>
                {Header}
                {contactOn && <ContactSheet isPay={false} onClose={() => setContactOn(false)} />}
                <div className={cx(styles.doneHead, styles.pcDone)} data-pc-done="">
                    <DoneIcon />
                    <h1 className={styles.doneTitle}>{nm && <>{nm}님의<br /></>}등록이 완료되었어요</h1>
                    {(st0 || branchKo) && <p className={styles.doneEntry}><IconCalendar />{(st0 ? md2(st0) : "") + (st0 && branchKo ? " " : "") + branchKo}에서 입실해요</p>}
                </div>
                <div className={cx(styles.doneBody, styles.pcDone)} data-pc-done="">
                    <div className={styles.receipt}>
                        {anc && <DiscountRows orig={doneOrig} discLabel={dName + " " + discPct + "%"} discTxt={doneDisc} />}
                        <div className={styles.rcRow}><span className={styles.rcTotalKey}>결제 금액</span><span className={styles.rcTotal}>{totalTxt}</span></div>
                        <div role="separator" className={styles.sepSoft} />
                        <div className={styles.payRow}><span>결제 일시</span><span>{link.paidAt ? dtLong(link.paidAt) : "-"}</span></div>
                    </div>
                    <div className={styles.infoWrap}><span className={styles.infoTitle}>등록 정보</span><InfoRows rows={infoRows} /></div>
                    <div className={styles.askWrap}>{AskRow}<a href="https://suneungsunbae.com/" className={styles.homeBtn}>홈으로</a></div>
                </div>
            </div></div>
        );
    }

    //----- 계좌이체 2단계: 확인 대기 -----
    if (stage === "waiting") {
        return (
            <div className={pageClass} data-pay="" ref={rootRef}><div className={cx(styles.inner, intro && styles.introIn)}>
                {Header}
                {contactOn && <ContactSheet isPay={false} onClose={() => setContactOn(false)} />}
                {proofZoom && proofUrl && <ProofZoom src={proofUrl} onClose={() => setProofZoom(false)} />}
                <div className={cx(styles.doneHead, styles.pcDone)} data-pc-done="">
                    <DoneIcon />
                    <h1 className={styles.doneTitle}>{nm && <>{nm}님의<br /></>}이체 내역을 받았어요</h1>
                </div>
                <div className={cx(styles.pcDone)} style={{ padding: "0 var(--px)" }} data-pc-done="">
                    <div className={styles.sentBox}>
                        <span className={styles.sentHead}><IconPerson /><span>담당자 확인 후 등록이 확정돼요</span></span>
                        <span className={styles.sentSub}>확정되면 입력한 연락처로 문자를 보내드려요</span>
                    </div>
                    {proofUrl && (
                        <button type="button" className={styles.sentProof} aria-label="제출한 이체 내역 크게 보기" onClick={() => setProofZoom(true)}>
                            <span role="img" aria-label="제출한 이체 내역" className={styles.sentThumb} style={{ backgroundImage: `url(${proofUrl})` }} />
                            <span className={styles.sentProofText}><span className={styles.sentProofTitle}>제출한 이체 내역</span><span className={styles.sentProofSub}>눌러서 크게 보기</span></span>
                            <span style={{ display: "flex", color: "#8B95A1" }}><IconChevronRight width={2} /></span>
                        </button>
                    )}
                </div>
                <div className={cx(styles.doneBody, styles.pcDone)} data-pc-done="">
                    <div className={styles.receipt}>
                        {anc && <DiscountRows orig={doneOrig} discLabel={dName + " " + discPct + "%"} discTxt={doneDisc} />}
                        <div className={styles.rcRow}><span className={styles.rcTotalKey}>등록 금액</span><span className={styles.rcTotal}>{totalTxt}</span></div>
                    </div>
                    <div className={styles.infoWrap}><span className={styles.infoTitle}>등록 정보</span><InfoRows rows={infoRows} /></div>
                    <div className={styles.askWrap}>{AskRow}<a href="https://suneungsunbae.com/" className={styles.homeBtn}>홈으로</a></div>
                </div>
            </div></div>
        );
    }

    //----- 계좌이체 1단계: 입금 대기 -----
    if (stage === "transfer" && transfer) {
        const bi = transfer.bankinfo;
        const hasProof = !!proofUrl && proofUploaded;
        return (
            <div className={pageClass} data-pay="" ref={rootRef}><div className={cx(styles.inner, intro && styles.introIn)}>
                {Header}
                {contactOn && <ContactSheet isPay={false} onClose={() => setContactOn(false)} />}
                {proofZoom && proofUrl && <ProofZoom src={proofUrl} onClose={() => setProofZoom(false)} />}
                <div className={cx(styles.doneHead, styles.pcDone)} data-pc-done="">
                    <DoneIcon />
                    <h1 className={styles.doneTitle}>{nm && <>{nm}님의<br /></>}등록이 접수되었어요</h1>
                </div>
                <TimerBar variant="transfer" expired={expired} to={linkTo} dueMark={dueMark} under10={under10} reserved={!!link.reservationNo} />
                <div className={cx(styles.doneBody, styles.pcDone)} data-pc-done="">
                    <div className={styles.vBox}>
                        <div className={styles.vBank}>
                            <span className={styles.rcKey}>입금 계좌</span>
                            <div className={styles.vBankRow}>
                                <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                                    <span className={styles.vBankName}>{bi ? bi.bankName + " " + bi.accountName : "-"}</span>
                                    <span className={styles.vAcct}>{bi ? bi.accountNumber : "-"}</span>
                                </div>
                                <button type="button" className={styles.copyBtn} onClick={copyAccount}>{copied ? "복사됨" : "복사"}</button>
                            </div>
                        </div>
                        <div role="separator" className={styles.vBoxSep} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {anc && <DiscountRows orig={doneOrig} discLabel={dName + " " + discPct + "%"} discTxt={doneDisc} dashed />}
                            <div className={styles.rcRow}><span className={styles.rcTotalKey}>입금할 금액</span><span className={styles.rcTotalMid}>{won(transfer.price)}</span></div>
                        </div>
                        <div role="separator" className={styles.vBoxSep} />
                        <div className={styles.rcRow}><span className={styles.rcKey} style={{ flex: "none" }}>입금 기한</span><span className={cx(styles.rcVal, expired && styles.vDueRed)} style={{ fontWeight: 500, color: expired ? undefined : "#0A1931", whiteSpace: "normal", textAlign: "right", wordBreak: "keep-all" }}>{dueAbsOf(dueDateObj)}{expired ? " 만료" : "까지"}</span></div>
                    </div>
                    <div className={styles.vUploadWrap}>
                        <span className={styles.infoTitle} style={{ letterSpacing: "-0.025em" }}>입금 후 바로 이체 내역 올리기</span>
                        <ul className={styles.vNotes}>
                            <li><IconInfo /><span>결제 링크가 <span className={styles.vRed}>닫히면</span> 올릴 수 없어요</span></li>
                            <li><IconInfo /><span>금액과 입금 일시가 보이게 캡처해 주세요</span></li>
                        </ul>
                        {!proofUrl && (
                            <label className={cx(styles.uploadLabel, expired && styles.uploadLabelOff)}>
                                <input type="file" accept="image/*" className={styles.hiddenInput} disabled={expired || proofUploading} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) uploadProof(f); e.target.value = ""; }} />
                                {proofUploading ? <JoyCircularProgress size="sm" /> : <IconUpload />}
                                <span>{proofUploading ? "올리는 중이에요" : "이체 완료 화면 캡처 올리기"}</span>
                            </label>
                        )}
                        {proofUrl && (
                            <>
                                <button type="button" className={styles.proofBtn} aria-label="올린 이체 내역 크게 보기" onClick={() => setProofZoom(true)}>
                                    <span role="img" aria-label="올린 이체 내역" className={styles.proofImg} style={{ backgroundImage: `url(${proofUrl})`, opacity: proofUploading ? 0.5 : 1 }} />
                                </button>
                                <div className={styles.proofActs}>
                                    <button type="button" className={styles.proofAct} onClick={() => setProofZoom(true)}><IconZoom />크게 보기</button>
                                    <label className={cx(styles.proofAct, expired && styles.uploadLabelOff)}>
                                        <input type="file" accept="image/*" className={styles.hiddenInput} disabled={expired || proofUploading} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) uploadProof(f); e.target.value = ""; }} />
                                        <IconRefresh />다시 올리기
                                    </label>
                                </div>
                            </>
                        )}
                        {proofError && <span className={styles.err}>{proofError}</span>}
                    </div>
                    <div className={styles.infoWrap}><span className={styles.infoTitle}>등록 정보</span><InfoRows rows={infoRows} /></div>
                    <div className={styles.askWrap}>{AskRow}</div>
                </div>
                <div className={cx(styles.vSticky, styles.pcDone)} data-pc-done="">
                    <div className={styles.vStickyIn}>
                        {proofTried && !hasProof && <span className={styles.vHint}>이체 내역 캡처를 먼저 올려주세요</span>}
                        <button type="button" className={cx(styles.submitBtn, (!hasProof || expired) && styles.submitBtnOff)} aria-disabled={!hasProof || expired} disabled={expired || finishLoading} onClick={submitProof}>
                            {finishLoading ? <JoyCircularProgress size="sm" /> : "이체 내역 제출하기"}
                        </button>
                    </div>
                </div>
            </div></div>
        );
    }

    //----- 결제 화면 -----
    const branchOpts = config.branches.map(b => ({ value: b.english, label: b.korean }));
    const monthly = (() => {
        const row = config.rates.prices[branch];
        if (!row) return [] as [string, number][];
        const per = row.periods || {};
        const pick = (pidKey: string) => { const v = per[pidKey]; return v != null && v > 0 ? v : row.base; };
        const pre = type === "student" ? pick("p4") : pick("p2");
        return [["프리윈터", pre], ["정규윈터", pick("p3")]] as [string, number][];
    })();
    const endList = endOpts.filter(e => st0 && dayNum(e) >= dayNum(st0)).map(e => ({ e, active: !!en0 && dayNum(e) === dayNum(en0) }));
    const hasOrig = price != null && original != null && original > price;
    const discLabelPay = calc && calc.usedBenefits.length ? calc.usedBenefits.map(c => c.name).join(" · ") + " 할인" : "할인";
    const emptyMsg = !branch ? "등록 지점을 고르면 계산돼요" : (!st0 || !en0) ? "등록 기간을 고르면 계산돼요" : "";
    const isEmpty = price == null && !!emptyMsg;
    //받는 혜택 카드 — 할인(확정 전엔 오늘 조건 맞는 혜택 미리), 좌석
    const benefitCards: Benefit[] = (() => {
        const discNames = calc && calc.usedBenefits.length
            ? calc.usedBenefits.map(c => ({ label: c.name + " 할인 " + c.pct + "%", sub: "자동으로 적용됐어요" }))
            : (pid ? config.rates.benefits.filter(c => c.auto !== false && !(branch && (c.excl || []).includes(branch)) && benefitMet(c, today) && (c.periodIds || []).includes(pid)).map(c => ({ label: c.name + " 할인 " + c.pct + "%", sub: "오늘 결제하면 자동으로 적용돼요" })) : []);
        const list: Benefit[] = discNames.map(d => ({ on: true, icon: "tag", label: d.label, sub: d.sub }));
        if (seatOk || (seatPerkOn && pid === "p2" && !st0)) list.push({ on: true, icon: "seat", label: "희망 좌석 선택권", sub: "결제 후 원하는 좌석을 직접 골라요" });
        else if (seatPerkOn && pid !== "p2") list.push({ on: false, icon: "lock", label: "희망 좌석 선택권", sub: "프리윈터로 등록하면 받을 수 있어요" });
        else if (seatLate) list.push({ on: false, icon: "lock", label: "희망 좌석 선택권", sub: "12.12까지 입실하면 받을 수 있어요" });
        return list;
    })();
    const hasHint = (!!firstMissing && payTried) || !!submitHint;
    const hintText = submitHint || (firstMissing ? firstMissing[1] : "");
    const hasOrderNote = !hasHint && !expired; // 예시 페이지에서도 환불 안내는 그대로 보임
    //연락처 두 칸 — 랜딩 v11과 같은 규칙: 010 기본·못 지움 / 입력하면서 대시 / 11자리 되면 학생→학부모 칸으로 이동, 학부모는 키보드 닫힘 / 포커스 시 커서 끝
    const phones = [["phone", "학생 연락처"], ["parent", "학부모 연락처"]] as [("phone" | "parent"), string][];
    const onPhoneChange = (k: "phone" | "parent") => (e: React.ChangeEvent<HTMLInputElement>) => {
        let d = digits(e.target.value).slice(0, 11);
        if (d.indexOf("010") !== 0) d = ("010" + d.replace(/^0?1?0?/, "")).slice(0, 11);
        const v = d === "010" ? "" : d;
        if (k === "phone") setStudentPhone(v); else setParentPhone(v);
        if (d.length === 11) {
            const el = e.target;
            try {
                if (k === "phone") { const next = document.querySelector('input[data-phone="parent"]') as HTMLInputElement | null; if (next) next.focus(); else el.blur(); }
                else el.blur();
            } catch (x) {}
        }
    };
    const onPhoneKey = (e: React.KeyboardEvent<HTMLInputElement>) => { if ((e.key === "Backspace" || e.key === "Delete") && digits((e.target as HTMLInputElement).value).length <= 3) e.preventDefault(); };
    const onPhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => { const el = e.target; setTimeout(() => { try { const n = el.value.length; el.setSelectionRange(n, n); } catch (x) {} }, 0); };
    const otpHint = otpOk ? "" : !otpNum ? "" : !phoneOk(otpNum) ? "번호를 끝까지 입력하면 인증할 수 있어요" : "";

    return (
        <div className={pageClass} data-pay="" ref={rootRef}><div className={cx(styles.inner, intro && styles.introIn)}>
            {Header}
            {contactOn && <ContactSheet isPay onClose={() => setContactOn(false)} />}
            {progClosed && prog && !sample && (
                <WaitPop
                    branchKo={branchKo}
                    progName={prog.name}
                    hasPre={pid !== "p2" && programs.some(p => p.pid === "p2" && !isClosedProg(p))}
                    onToPre={() => { setProgram("p2"); setStart(null); setEnd(null); setView(null); setOpenRow(null); }}
                    onOtherBranch={() => { setBranch(""); setStart(null); setEnd(null); setOpenRow(null); }}
                    onWait={() => {
                        track("Winter Pay Closed To Waiting", { form_type: "winterpay", payment_id: id, branch, program: pid });
                        window.location.href = `/winterregister?prog=${pid === "p2" ? "pre" : "regular"}&branch=${encodeURIComponent(branchKo.replace(/점$/, ""))}&wait=1`;
                    }}
                />
            )}

            <div className={styles.pcGrid} data-pc-grid="">
            <div className={styles.pcMain} data-pc-main="">
            <div className={styles.titleWrap}>
                <InviteCard no={link.reservationNo || null} name={link.name || null} />
            </div>

            <TimerBar variant="pay" expired={expired} to={linkTo} dueMark={dueMark} under10={under10} reserved={!!link.reservationNo} />

            {/* 등록 지점 */}
            <div className={styles.sec} data-sec="branch">
                <span className={styles.secTitle}>등록 지점</span>
                <div className={styles.col}>
                    <div className={styles.field}>
                        <span className={styles.label}>지점</span>
                        <Dropdown value={branch} options={branchOpts} placeholder="지점을 선택해주세요" open={openDd === "branch"} onToggle={() => setOpenDd(openDd === "branch" ? null : "branch")} onPick={v => { setBranch(v); setOpenDd(null); setStart(null); setEnd(null); if (wantProgram && wantProgram !== program) setProgram(wantProgram); }} />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>학생 구분</span>
                        <SegGroup items={[{ value: "general", label: "N수생" }, { value: "student", label: "재학생" }]} value={type} onChange={v => setType(v as WinterStudentType)} />
                    </div>
                    {branch && (
                        <div className={styles.monthlyBox}>
                            <span className={styles.monthlyHead}>1개월 기준 등록비</span>
                            {monthly.map(([label, v]) => <div className={styles.monthlyRow} key={label}><span className={styles.monthlyLabel}>{label}</span><span className={styles.monthlyPrice}>{v ? won(v) : "-"}</span></div>)}
                            <span className={styles.mute}>입실일 기준으로 일할 계산돼요.</span>
                        </div>
                    )}
                </div>
            </div>

            {/* 등록 기간 */}
            {branch && (
                <div className={styles.sec} data-sec="period">
                    <span className={styles.secTitle}>등록 기간</span>
                    <div className={styles.field}>
                        <span className={styles.label}>언제부터 다닐까요?</span>
                        <div role="radiogroup" className={styles.progList}>
                            {programs.map(p => {
                                const a = p.pid === pid, isPre = p.pid === "p2";
                                const closed = isClosedProg(p);
                                const r = startRangeOf(config, p, branch), sd = toDate(r ? r.from : p.startFrom);
                                return (
                                    <button type="button" role="radio" aria-checked={a} aria-disabled={closed} key={p.pid} className={cx(styles.prog, a && styles.progOn, closed && styles.progOff)} onClick={() => { if (closed) return; if (!a) { setProgram(p.pid); setWantProgram(p.pid); setStart(null); setEnd(null); setView(null); setOpenRow(null); } }}>
                                        <span className={styles.radio}><span className={styles.radioDot} /></span>
                                        <span className={styles.progHead}>
                                            <span className={styles.progTitle}>{p.name}</span>
                                            <span className={styles.progPeriod}>{closed ? "모집 마감" : (sd.getMonth() + 1) + "." + sd.getDate() + " 시작"}</span>
                                            {closed && <span className={styles.closedTag}>대기 신청만 가능</span>}
                                            {!closed && isPre && seatPerkOn && <span className={styles.perkTag}>프리윈터 전용 혜택</span>}
                                        </span>
                                        {isPre && seatPerkOn && (
                                            <span className={styles.perkBox}>
                                                <IconSeat />
                                                <span><span className={styles.perkTitle}>희망 좌석 선택권</span><span className={styles.perkSub}>원하는 좌석을 직접 골라요</span></span>
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.field}>
                        <button type="button" data-start-row="" aria-expanded={openRow === "start"} className={cx(styles.rowSel, openRow === "start" && styles.rowSelOpen)} onClick={() => (openRow === "start" ? setOpenRow(null) : openPicker("start"))}>
                            <span className={styles.rowLabel}>시작일</span>
                            <span className={cx(styles.rowVal, !st0 && styles.rowValEmpty)}>{st0 ? dshort(st0) : "선택"}<IconChevronRight /></span>
                        </button>
                        {seatLate && (
                            <div className={styles.seatNote}>
                                <span className={styles.seatNoteLeft}>
                                    <IconLock size={16} stroke="#8B95A1" />
                                    <span><span className={styles.seatNoteTitle}>희망 좌석 선택권 미적용</span><span className={styles.seatNoteSub}>12.12까지 입실하면 받을 수 있어요</span></span>
                                </span>
                                <button type="button" className={styles.seatCta} onClick={() => openPicker("start")}>날짜 바꾸기</button>
                            </div>
                        )}
                        {openRow === "start" && (
                            <Calendar title={calView.y + "년 " + (calView.m + 1) + "월"} cells={calCells} seatLegend={seatCal}
                                prevVis={calIdx > minD.getFullYear() * 12 + minD.getMonth()} nextVis={calIdx < maxD.getFullYear() * 12 + maxD.getMonth()}
                                onPrev={() => calGo(-1)} onNext={() => calGo(1)} />
                        )}
                        <button type="button" aria-expanded={openRow === "end"} disabled={!st0} className={cx(styles.rowSel, openRow === "end" && styles.rowSelOpen)} onClick={() => (openRow === "end" ? setOpenRow(null) : openPicker("end"))}>
                            <span className={styles.rowLabel}>종료일</span>
                            <span className={cx(styles.rowVal, !en0 && styles.rowValEmpty)}>{en0 ? dshort(en0) : "선택"}<IconChevronRight /></span>
                        </button>
                        {openRow === "end" && st0 && (
                            <div role="radiogroup" className={styles.endList} data-endlist="">
                                {endList.map(eo => (
                                    <button type="button" role="radio" aria-checked={eo.active} key={ymd(eo.e)} className={cx(styles.endItem, eo.active && styles.endItemOn)} onClick={() => { setEnd(eo.e); setOpenRow(null); }}>
                                        <span className={styles.endDate}>{dshort(eo.e)}</span>
                                        <span className={styles.endIcon}>{eo.active ? <IconCheck width={1.75} /> : <IconChevronRight />}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 학생 정보 */}
            <div className={styles.sec} data-sec="contact">
                <span className={styles.secTitle}>학생 정보</span>
                <div className={styles.col}>
                    <div className={styles.gridAuto}>
                        <div className={styles.field}>
                            <span className={styles.label}>학생 이름</span>
                            <input value={name} onChange={e => setName(e.target.value)} placeholder="이름" autoComplete="off" enterKeyHint="next" className={cx(styles.input, payTried && !name.trim() && styles.inputErr)} />
                            {payTried && !name.trim() && <span className={styles.err}>학생 이름을 입력해주세요</span>}
                        </div>
                        <div className={styles.field}>
                            <span className={styles.label}>성별</span>
                            <SegGroup items={[{ value: "male", label: "남학생" }, { value: "female", label: "여학생" }]} value={gender} onChange={v => setGender(v as "male" | "female")} />
                        </div>
                    </div>
                    {phones.map(([k, label]) => {
                        const val = k === "phone" ? studentPhone : parentPhone;
                        const num = digits(val);
                        const err = num.length >= 3 && !num.startsWith("010") ? "010으로 시작하는 번호를 입력해주세요" : payTried && !phoneOk(num) ? label + "를 입력해주세요" : "";
                        return (
                            <div className={styles.field} key={k}>
                                <span className={styles.label}>{label}</span>
                                <input data-phone={k} value={fmtPhone(val || "010")} onChange={onPhoneChange(k)} onKeyDown={onPhoneKey} onFocus={onPhoneFocus} placeholder="010-0000-0000" type="tel" inputMode="numeric" autoComplete={k === "phone" ? "tel" : "off"} className={cx(styles.input, err && styles.inputErr)} />
                                {err && <span className={styles.err}>{err}</span>}
                            </div>
                        );
                    })}
                    <div className={styles.field} data-otp="">
                        <span className={styles.label}>인증받을 번호</span>
                        <SegGroup items={[{ value: "parent", label: "학부모 번호로" }, { value: "phone", label: "학생 번호로" }]} value={otpTarget} onChange={v => { setOtpTarget(v as "phone" | "parent"); setOtpKey(null); setOtpCode(""); setOtpErr(""); }} />
                        <div className={styles.phoneRow}>
                            <input value={otpNum ? fmtPhone(otpNum) : ""} readOnly tabIndex={-1} aria-label="인증받을 번호" placeholder={(otpTarget === "phone" ? "학생" : "학부모") + " 연락처를 먼저 입력해주세요"} className={styles.otpNumInput} />
                            <button type="button" className={cx(styles.certBtn, (otpOk || phoneOk(otpNum)) && styles.certBtnReady, otpOk && styles.certBtnDone)} onClick={() => sendCert(otpNum, otpTarget)}>
                                {otpOk ? <><IconCheck size={14} width={2.25} />인증 완료</> : otpSent ? "다시 받기" : "인증번호 받기"}
                            </button>
                        </div>
                        {otpHint && <span className={styles.codeMsg}>{otpHint}</span>}
                        {otpSent && (
                            <>
                                <input ref={codeInputRef} value={otpCode} onChange={e => { const v = digits(e.target.value).slice(0, 6); setOtpCode(v); setOtpErr(""); if (v.length === 6) confirmCert(otpNum, v); }} placeholder="인증번호 6자리" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={cx(styles.input, otpErr && styles.inputErr)} />
                                <span className={cx(styles.codeMsg, otpErr && styles.codeMsgErr)}>{otpErr || "인증번호를 보냈어요."}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* 결제 수단 */}
            <div className={styles.sec} data-sec="method">
                <span className={styles.secTitle}>결제 수단</span>
                <div role="radiogroup" className={styles.progList}>
                    {([["card", "신용·체크카드"], ["transfer", "계좌이체"]] as [("card" | "transfer"), string][]).map(([k, label]) => (
                        <button type="button" role="radio" aria-checked={payMethod === k} key={k} className={cx(styles.method, payMethod === k && styles.methodOn)} onClick={() => setPayMethod(k)}>
                            <span className={styles.methodRing}><span className={styles.methodDot} /></span>
                            <span className={styles.methodLabel}>{label}</span>
                        </button>
                    ))}
                </div>
            </div>
            </div>

            {/* 결제 금액 (PC 2단에선 오른쪽 고정 패널) */}
            <div className={styles.pcAside} data-pc-aside="">
            <div className={styles.sec} data-sec="amount">
                <span className={styles.secTitle}>결제 금액</span>
                <div className={styles.totalBox} data-pc-total="">
                    {hasOrig && <DiscountRows orig={won(original as number)} discLabel={discLabelPay} discTxt={"−" + won((original as number) - (price as number))} />}
                    <div className={styles.rcRow}>
                        <span className={styles.rcTotalKey}>최종 결제 금액</span>
                        {isEmpty ? <span className={styles.rcEmpty}>{emptyMsg}</span> : <span className={styles.rcTotal} style={{ color: price == null ? "#B0B8C1" : undefined }}>{price == null ? "-" : won(price)}</span>}
                    </div>
                    {splitTxt && <span className={styles.split}>{splitTxt}</span>}
                </div>
                {benefitCards.length > 0 && (
                    <div className={styles.bnWrap}>
                        <span className={styles.bnHeadLabel}>받는 혜택</span>
                        <div className={styles.bnList}>{benefitCards.map(b => <BenefitCard b={b} key={b.label} />)}</div>
                    </div>
                )}
                {calc && calc.rows.length > 0 && (
                    <button type="button" aria-expanded={showRows} className={cx(styles.rowsToggle, showRows && styles.rowsToggleOpen)} onClick={() => setShowRows(s => !s)}><IconChevronDown stroke="currentColor" />월별 계산 내역 {showRows ? "접기" : "보기"}</button>
                )}
                {calc && showRows && (
                    <div className={styles.rowsList}>
                        {calc.rows.map((r, i) => (
                            <div className={styles.rowItem} key={i}>
                                <div><span className={styles.rowMain}>{r.month}</span><span className={styles.rowSub}>{r.range}</span></div>
                                <div><span className={styles.rowMain}>{r.amount}</span><span className={styles.rowSub}>{r.days}</span></div>
                            </div>
                        ))}
                        <div className={styles.noteBox}>
                            <span className={styles.noteHead}>계산 방법</span>
                            <div className={styles.noteRow}><span>한 달 전체</span><span>월 금액 그대로</span></div>
                            <div className={styles.noteRow}><span>일부만 다닌 달</span><span>월 금액 ÷ 그 달 일수 × 다닌 날</span></div>
                            {calc.usedBenefits.length > 0 && <div className={styles.noteRow}><span>할인</span><span>월 금액에 먼저 적용</span></div>}
                            <div className={styles.noteRow}><span>끝자리</span><span>10원 미만은 버려요</span></div>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.pcNote} data-pc-note=""><p>결제하면 입실 안내 문자를 학생·학부모 번호로 보내드려요.</p></div>

            <div className={styles.stickyBar} data-pc-bar="">
                {hasHint && <span className={styles.hint}>{hintText}</span>}
                {hasOrderNote && <span className={styles.orderNote} data-pc-refund-pill=""><IconCheck size={14} width={2.25} />{sample ? "예시 페이지예요. " : ""}{price != null && st0 && en0 ? "입실 전까지 전액 환불" : "입실 전까지 전액 환불돼요"}</span>}
                <button type="button" className={cx(styles.payBtn, (expired || sample) && styles.payBtnOff)} disabled={expired || sample || submitLoading} onClick={onPay}>
                    {submitLoading ? <JoyCircularProgress size="sm" /> : <>
                        {seatOk && price != null && <span className={styles.payBadge}>좌석 선택권</span>}
                        {hasOrig && <span className={styles.payOrig} data-pc-btn-orig="">{won(original as number)}</span>}
                        <span className={styles.payLabel}>{sample ? "실제 결제 링크는 10/5 발송" : price == null ? "결제하기" : won(price) + " 결제하기"}</span>
                    </>}
                </button>
            </div>
            </div>
            </div>
        </div></div>
    );
};

export default WinterPay;
